/* RMH3 Dental theme — vanilla interactivity (no framework).
   Reimplements the prototype's stateful bits: steppers, tabs, view toggle,
   shade/pack selectors, cart drawer, mobile nav, and density/theme/accent
   tokens driven by theme settings + an optional operator preview switch. */
(function () {
  'use strict';

  var ACCENT_MAP = {
    '#0e57f0': { s: '#0a3fc0', soft: '#e6efff', bright: '#3b7bff' },
    '#0f766e': { s: '#0a564f', soft: '#dcf5f1', bright: '#16a394' },
    '#1f2937': { s: '#0f172a', soft: '#e5e7eb', bright: '#3b4760' },
    '#b45309': { s: '#854006', soft: '#fdf2dd', bright: '#e0790f' },
    '#6d28d9': { s: '#4c1d95', soft: '#ede9fe', bright: '#8b5cf6' },
    '#3051a4': { s: '#22397a', soft: '#eaeff8', bright: '#3f68c4' }
  };
  var RMH3 = window.RMH3 || {};
  var activePanel = null;
  var lastPanelTrigger = null;
  var lastCartCount = null;
  var cartChangeTimers = {};

  RMH3.cart = RMH3.cart && RMH3.cart.subscribe ? RMH3.cart : {
    state: null,
    subs: [],
    subscribers: [],
    set: function (cart) {
      this.state = cart || emptyCart();
      this.subscribers = this.subs;
      this.subs.slice().forEach(function (fn) { fn(cart || emptyCart()); });
    },
    subscribe: function (fn) {
      this.subs.push(fn);
      this.subscribers = this.subs;
      if (this.state) fn(this.state);
    }
  };

  function applyAccent(accent) {
    var a = (accent || '#0e57f0').toLowerCase();
    var p = ACCENT_MAP[a] || ACCENT_MAP['#0e57f0'];
    var r = document.documentElement;
    r.style.setProperty('--accent', a);
    r.style.setProperty('--accent-strong', p.s);
    r.style.setProperty('--accent-soft', p.soft);
    r.style.setProperty('--accent-bright', p.bright);
    r.style.setProperty('--ring', '0 0 0 3px ' + a + '30');
  }

  // Apply theme settings written to <html data-*> by the layout.
  function bootTokens() {
    var r = document.documentElement;
    if (r.dataset.accent) applyAccent(r.dataset.accent);
    // density + theme attributes are set server-side; nothing else needed.
  }

  // Steppers
  function bindSteppers(root) {
    (root || document).querySelectorAll('[data-stepper]').forEach(function (el) {
      if (el.__bound) return; el.__bound = true;
      var input = el.querySelector('input');
      if (!input) return;
      var min = parseInt(el.getAttribute('data-min') || '0', 10);
      var max = parseInt(el.getAttribute('data-max') || '9999', 10);
      function sync() {
        var v = parseInt(input.value, 10);
        if (!Number.isFinite(v)) v = min;
        v = Math.max(min, Math.min(max, v));
        input.value = v;
        el.querySelectorAll('button[data-dir="down"]').forEach(function (btn) { btn.disabled = v <= min; });
        el.querySelectorAll('button[data-dir="up"]').forEach(function (btn) { btn.disabled = v >= max; });
      }
      el.querySelectorAll('button').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var v = parseInt(input.value, 10) || 0;
          v += btn.dataset.dir === 'up' ? 1 : -1;
          input.value = Math.max(min, Math.min(max, v));
          sync();
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
      input.addEventListener('change', sync);
      sync();
    });
  }

  // Tabs — [data-tabs] container with [data-tab="key"] buttons + [data-panel="key"]
  function bindTabs(root) {
    (root || document).querySelectorAll('[data-tabs]').forEach(function (group) {
      if (group.__bound) return; group.__bound = true;
      group.querySelectorAll('[data-tab]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var key = btn.getAttribute('data-tab');
          group.querySelectorAll('[data-tab]').forEach(function (b) { b.classList.toggle('active', b === btn); });
          var scope = group.closest('[data-tabs-scope]') || document;
          scope.querySelectorAll('[data-panel]').forEach(function (p) {
            p.classList.toggle('active', p.getAttribute('data-panel') === key);
          });
        });
      });
    });
  }

  // Generic single-select button group: [data-select] with [data-opt]
  function bindSelectGroups(root) {
    (root || document).querySelectorAll('[data-select]').forEach(function (group) {
      if (group.__bound) return; group.__bound = true;
      group.addEventListener('click', function (e) {
        var opt = e.target.closest('[data-opt]');
        if (!opt || !group.contains(opt)) return;
        group.querySelectorAll('[data-opt]').forEach(function (o) { o.classList.toggle('active', o === opt); });
        var target = group.getAttribute('data-select-out');
        if (target) { var out = document.querySelector(target); if (out) out.textContent = opt.getAttribute('data-label') || opt.textContent.trim(); }
      });
    });
  }

  // PLP view toggle: [data-view-toggle] buttons control [data-view-panel]
  function bindViewToggle(root) {
    (root || document).querySelectorAll('[data-view-toggle]').forEach(function (group) {
      if (group.__bound) return; group.__bound = true;
      var scope = group.closest('.manufacturers-layout') || group.closest('.container-wide') || document;
      function setView(activeBtn) {
        if (!activeBtn) return;
        var v = activeBtn.getAttribute('data-view');
        group.querySelectorAll('button[data-view]').forEach(function (b) {
          var active = b === activeBtn;
          b.classList.toggle('active', active);
          b.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        scope.querySelectorAll('[data-view-panel]').forEach(function (p) {
          p.style.display = p.getAttribute('data-view-panel') === v ? '' : 'none';
        });
      }
      setView(group.querySelector('button[data-view].active') || group.querySelector('button[data-view]'));
      group.querySelectorAll('button[data-view]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          setView(btn);
        });
      });
    });
  }

  function bindMegaMenu(root) {
    var scope = root || document;
    var trigger = scope.querySelector ? scope.querySelector('.nav-categories-trigger') : document.querySelector('.nav-categories-trigger');
    var menu = document.getElementById('mega-menu');
    if (!trigger || !menu || trigger.__boundMegaMenu) return;
    trigger.__boundMegaMenu = true;
    var nav = trigger.closest('nav');
    var hoverable = window.matchMedia && window.matchMedia('(hover: hover)').matches;
    var suppressFocusOpen = false;
    // At burger widths the mobile accordion owns category nav; the mega-menu
    // stays inert and the Categories link navigates like a plain link.
    function isMobileNav() {
      return window.matchMedia && window.matchMedia('(max-width: 780px)').matches;
    }
    function open() {
      if (isMobileNav()) return;
      menu.classList.add('open');
      trigger.setAttribute('aria-expanded', 'true');
    }
    function close() {
      menu.classList.remove('open');
      trigger.setAttribute('aria-expanded', 'false');
      menu.querySelectorAll('.mm-dept--open').forEach(function (d) { setDeptOpen(d, false); });
    }
    function isOpen() { return menu.classList.contains('open'); }

    // Sub-flyout helpers: flip to the left when the default right-side
    // position would overflow the viewport; sync aria-expanded on the
    // department link.
    function setDeptOpen(dept, on) {
      dept.classList.toggle('mm-dept--open', !!on);
      var link = dept.querySelector(':scope > a[aria-haspopup]');
      if (link) link.setAttribute('aria-expanded', on ? 'true' : 'false');
    }
    function placeSubs(dept) {
      var subs = dept.querySelector('.mm-subs');
      if (!subs) return;
      subs.classList.remove('mm-subs--flip');
      var width = subs.offsetWidth || 230;
      if (dept.getBoundingClientRect().right + width + 10 > document.documentElement.clientWidth) {
        subs.classList.add('mm-subs--flip');
      }
    }
    menu.querySelectorAll('.mm-dept').forEach(function (dept) {
      if (!dept.querySelector('.mm-subs')) return;
      dept.addEventListener('mouseenter', function () { placeSubs(dept); setDeptOpen(dept, true); });
      dept.addEventListener('mouseleave', function () { setDeptOpen(dept, false); });
      dept.addEventListener('focusin', function () { placeSubs(dept); setDeptOpen(dept, true); });
      dept.addEventListener('focusout', function (e) {
        if (!dept.contains(e.relatedTarget)) setDeptOpen(dept, false);
      });
    });

    if (hoverable) {
      trigger.addEventListener('mouseenter', open);
      menu.addEventListener('mouseleave', close);
      if (nav) {
        nav.addEventListener('mouseleave', function (e) {
          if (!menu.contains(e.relatedTarget) && e.relatedTarget !== trigger) close();
        });
      }
    } else {
      // Touch: first tap on a department link expands its flyout; second
      // tap (now marked open) follows the link.
      menu.addEventListener('click', function (e) {
        var link = e.target.closest('.mm-dept > a');
        if (!link || !menu.contains(link)) return;
        var dept = link.parentElement;
        if (!dept.querySelector('.mm-subs') || dept.classList.contains('mm-dept--open')) return;
        e.preventDefault();
        menu.querySelectorAll('.mm-dept--open').forEach(function (d) { if (d !== dept) setDeptOpen(d, false); });
        placeSubs(dept);
        setDeptOpen(dept, true);
      });
    }
    trigger.addEventListener('focus', function () {
      // Escape refocuses the trigger; don't let that focus instantly reopen.
      if (suppressFocusOpen) {
        suppressFocusOpen = false;
        return;
      }
      open();
    });
    // First click opens the panel when it isn't open yet (covers touch,
    // where hover can't open it); a second activation follows the link.
    // At burger widths the link navigates normally — the accordion owns mobile.
    trigger.addEventListener('click', function (e) {
      if (isMobileNav()) return;
      if (!isOpen()) {
        e.preventDefault();
        open();
      }
    });
    // Dismiss on outside tap/click and when keyboard focus leaves the nav.
    document.addEventListener('click', function (e) {
      if (!isOpen()) return;
      if (menu.contains(e.target) || trigger.contains(e.target)) return;
      close();
    });
    document.addEventListener('focusin', function (e) {
      if (!isOpen()) return;
      if (menu.contains(e.target) || (nav && nav.contains(e.target))) return;
      close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen()) {
        close();
        suppressFocusOpen = true;
        trigger.focus();
      }
    });
  }

  // Child-category tile strip: its template slot renders after main (so the
  // crumb + title never shift on load), then it is slotted in just above the
  // product grid — i.e. below the breadcrumb/title area. No-JS keeps it after
  // the grid, which still reads fine.
  function bindChildCategoriesPlacement() {
    document.querySelectorAll('[data-rmh3-move-before]').forEach(function (el) {
      if (el.__rmh3Moved) return;
      var target = document.querySelector(el.getAttribute('data-rmh3-move-before'));
      if (!target || !target.parentNode) return;
      el.__rmh3Moved = true;
      el.classList.add('rmh3-child-categories--inline');
      target.parentNode.insertBefore(el, target);
    });
  }

  // Sticky header: add a drop shadow once the page scrolls so the pinned
  // header separates from content. rAF-throttled, passive.
  function bindStickyHeader() {
    var header = document.querySelector('.app-header');
    if (!header || header.__stickyBound) return;
    header.__stickyBound = true;
    var ticking = false;
    function sync() {
      ticking = false;
      header.classList.toggle('is-scrolled', (window.scrollY || 0) > 4);
    }
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(sync);
    }, { passive: true });
    sync();
  }

  function syncActiveNav() {
    var links = document.querySelectorAll('#nav-links-center a[data-nav-key]');
    if (!links.length) return;
    var path = window.location.pathname;
    var params = new URLSearchParams(window.location.search);
    var key = '';
    if (path === '/' || path === '') key = 'home';
    else if (path === '/collections/all' && params.get('view') === 'manufacturers') key = 'manufacturers';
    else if (path.indexOf('/collections/vendors') === 0 && params.get('view') === 'manufacturers') key = 'manufacturers';
    else if (path.indexOf('/pages/manufacturers') === 0) key = 'manufacturers';
    else if (path.indexOf('/collections/deals') === 0) key = 'deals';
    else if (path.indexOf('/collections/best-seller') === 0) key = 'best-sellers';
    else if (path.indexOf('/pages/about-us') === 0 || path.indexOf('/pages/about') === 0) key = 'about';
    else if (path.indexOf('/collections') === 0) key = 'categories';
    links.forEach(function (link) {
      link.classList.toggle('active', key && link.dataset.navKey === key);
    });
  }

  // Cart drawer + mobile nav + generic [data-toggle="#id"]
  function focusableIn(el) {
    return Array.prototype.slice.call(el.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])')).filter(function (node) {
      return node.offsetWidth || node.offsetHeight || node === document.activeElement;
    });
  }

  function syncToggleTriggers(el, expanded) {
    if (!el.id) return;
    document.querySelectorAll('[data-toggle="#' + el.id + '"]').forEach(function (t) {
      t.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  function openPanel(el, trigger) {
    if (!el) return;
    if (activePanel && activePanel !== el) closePanel(activePanel);
    var bd = el.getAttribute('data-backdrop') && document.querySelector(el.getAttribute('data-backdrop'));
    el.classList.add('open');
    el.setAttribute('aria-hidden', 'false');
    syncToggleTriggers(el, true);
    if (bd) bd.classList.add('open');
    if (el.getAttribute('role') === 'dialog') {
      activePanel = el;
      lastPanelTrigger = trigger || document.activeElement;
      window.setTimeout(function () {
        var first = focusableIn(el)[0] || el;
        if (first && first.focus) first.focus({ preventScroll: true });
      }, 0);
    }
  }

  function closePanel(el) {
    if (!el) return;
    el.classList.remove('open');
    // Only dialogs stay aria-hidden when closed; non-dialog panels (e.g. the
    // mobile nav) are visible again at desktop widths once .open is removed.
    if (el.getAttribute('role') === 'dialog') el.setAttribute('aria-hidden', 'true');
    else el.removeAttribute('aria-hidden');
    syncToggleTriggers(el, false);
    var bd = el.getAttribute('data-backdrop') && document.querySelector(el.getAttribute('data-backdrop'));
    if (bd) bd.classList.remove('open');
    if (activePanel === el) {
      activePanel = null;
      if (lastPanelTrigger && lastPanelTrigger.focus) lastPanelTrigger.focus({ preventScroll: true });
      lastPanelTrigger = null;
    }
  }

  function bindToggles(root) {
    (root || document).querySelectorAll('[data-toggle]').forEach(function (btn) {
      if (btn.__bound) return; btn.__bound = true;
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var sel = btn.getAttribute('data-toggle');
        var el = document.querySelector(sel);
        if (!el) return;
        if (el.classList.contains('open')) closePanel(el);
        else openPanel(el, btn);
      });
    });
    (root || document).querySelectorAll('[data-close]').forEach(function (btn) {
      if (btn.__bound) return; btn.__bound = true;
      btn.addEventListener('click', function () {
        var el = document.querySelector(btn.getAttribute('data-close'));
        if (el) closePanel(el);
      });
    });
    (root || document).querySelectorAll('.drawer-backdrop[data-close]').forEach(function (bd) {
      if (bd.__boundBackdrop) return; bd.__boundBackdrop = true;
      bd.addEventListener('click', function () {
        var el = document.querySelector(bd.getAttribute('data-close'));
        if (el) closePanel(el);
      });
    });
  }

  // Optional operator preview switch (anon | verified) — design parity with
  // the prototype's state toggle. Off in production unless [data-preview-switch].
  function bindPreviewSwitch() {
    document.querySelectorAll('[data-preview-mode]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        document.body.setAttribute('data-mode', btn.getAttribute('data-preview-mode'));
        document.querySelectorAll('[data-preview-mode]').forEach(function (b) { b.classList.toggle('active', b === btn); });
      });
    });
  }

  function bindProductZoom(root) {
    (root || document).querySelectorAll('[data-product-zoom]').forEach(function (shell) {
      if (shell.__boundZoom) return; shell.__boundZoom = true;
      var slot = shell.querySelector('.product-zoom-slot');
      if (!slot) return;
      var zoom = 1;
      var reset = shell.querySelector('[data-zoom-reset]');

      function setZoom(next) {
        zoom = Math.max(0.7, Math.min(2.5, Math.round(next * 100) / 100));
        slot.style.setProperty('--media-zoom', zoom);
        if (reset) reset.textContent = Math.round(zoom * 100) + '%';
      }

      var zoomOut = shell.querySelector('[data-zoom-out]');
      var zoomIn = shell.querySelector('[data-zoom-in]');
      if (zoomOut) zoomOut.addEventListener('click', function () { setZoom(zoom - 0.15); });
      if (zoomIn) zoomIn.addEventListener('click', function () { setZoom(zoom + 0.15); });
      if (reset) reset.addEventListener('click', function () { setZoom(1); });
      slot.addEventListener('click', function () { setZoom(zoom < 1.5 ? zoom + 0.25 : 1); });
      setZoom(1);
    });
  }

  function catalogImageFromShopifyFile(src) {
    try {
      var u = new URL(src, window.location.href);
      if (u.pathname.indexOf('/cdn/shop/files/') !== -1) return '';
      var match = u.pathname.match(/\/files\/([^/?#]+)$/);
      if (!match) return '';
      var filename = decodeURIComponent(match[1]);
      var slug = filename.split('_')[0];
      if (!slug || !filename) return '';
      return 'https://rmh3.app/images/products/' + encodeURIComponent(slug) + '/' + encodeURIComponent(filename);
    } catch (e) {
      return '';
    }
  }

  function setProductImageSource(img, src) {
    if (!img || !src) return false;
    var next = new URL(src, window.location.href).href;
    if (img.src === next) return false;
    if (next.indexOf('https://rmh3.app/') === 0) {
      img.crossOrigin = 'anonymous';
      img.referrerPolicy = 'no-referrer';
    }
    img.classList.remove('is-broken');
    img.src = next;
    return true;
  }

  function cleanText(text) {
    return (text || '').replace(/[<>&"]/g, '').trim();
  }

  function productImageFallback(img) {
    var slot = img && img.closest('.img-slot');
    if (!slot || slot.querySelector('.product-image-fallback')) return;
    var card = img.closest('.pcard, .pcard-list');
    var titleEl = card && card.querySelector('.ptitle');
    var skuEl = card && card.querySelector('.pmeta');
    var title = cleanText(titleEl ? titleEl.textContent : img.alt) || 'Image pending';
    var sku = cleanText(img.getAttribute('data-rmh3-sku') || (skuEl && skuEl.textContent));
    img.classList.add('is-broken');
    var fallback = document.createElement('div');
    fallback.className = 'product-image-fallback';
    fallback.innerHTML = '<strong>' + title + '</strong>' + (sku ? '<small>' + sku + '</small>' : '');
    slot.appendChild(fallback);
  }

  function repairProductImage(img) {
    if (!img || img.dataset.rmh3ImageOk === '1') return;
    if (img.complete && img.naturalWidth > 0) {
      img.dataset.rmh3ImageOk = '1';
      return;
    }
    if (img.dataset.rmh3ApiTried !== '1') {
      var apiAttr = img.getAttribute('data-rmh3-api-src') || '';
      var apiSrc = catalogImageFromShopifyFile(apiAttr) || apiAttr || catalogImageFromShopifyFile(img.currentSrc || img.src) || catalogImageFromShopifyFile(img.getAttribute('data-rmh3-original-src'));
      if (apiSrc) {
        img.dataset.rmh3ApiTried = '1';
        if (setProductImageSource(img, apiSrc)) return;
      }
    }
    productImageFallback(img);
  }

  function bindProductImages(root) {
    (root || document).querySelectorAll('[data-rmh3-product-image]').forEach(function (img) {
      if (!img.__boundProductImage) {
        img.__boundProductImage = true;
        img.addEventListener('load', function () {
          if (img.naturalWidth > 0) img.dataset.rmh3ImageOk = '1';
        });
        img.addEventListener('error', function () { repairProductImage(img); });
      }
      // NOTE: never proactively "repair" a healthy Shopify CDN URL. /cart.js
      // serves images from cdn.shopify.com/s/files/…/files/… — a branch here
      // used to treat that shape as a legacy catalog URL and rewrite EVERY
      // cart thumbnail to https://rmh3.app/images/products/… before it even
      // loaded, 404ing on the money page (shopper-report v2 fresh finding #2).
      // Genuinely broken images are handled reactively by the error listener
      // above (ERP fallback first, branded monogram second).
      if (img.getAttribute('data-rmh3-api-src') && img.src !== new URL(img.getAttribute('data-rmh3-api-src'), window.location.href).href && img.dataset.rmh3ApiTried !== '1') {
        repairProductImage(img);
      } else if (img.complete && img.naturalWidth === 0) {
        repairProductImage(img);
      }
    });
  }

  function emptyCart() {
    return {
      item_count: 0,
      total_price: 0,
      items_subtotal_price: 0,
      original_total_price: 0,
      items: []
    };
  }

  function escapeHtml(text) {
    return String(text == null ? '' : text).replace(/[&<>"']/g, function (ch) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch];
    });
  }

  function formatMoney(cents) {
    var n = Number(cents || 0);
    if (!Number.isFinite(n)) n = 0;
    // toLocaleString adds thousands separators to match the Liquid `| money`
    // filter ("$4,505.00"); toFixed(2) alone dropped them ("$4505.00").
    return '$' + (n / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function cartRoot() {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  function cartEndpoint(path) {
    return cartRoot() + String(path || '').replace(/^\//, '');
  }

  function cartRequest(path, body, options) {
    var opts = {
      method: body == null ? 'GET' : 'POST',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    };
    if (options && options.method) opts.method = options.method;
    if (body != null) {
      opts.headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(body);
    }
    return fetch(cartEndpoint(path), opts).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (json) {
        if (!res.ok) {
          json.status = json.status || res.status;
          throw json;
        }
        return json;
      });
    });
  }

  function readCart() {
    return cartRequest('/cart.js');
  }

  function setCartFromResponse(data) {
    if (data && Array.isArray(data.items) && typeof data.item_count !== 'undefined') {
      RMH3.cart.set(data);
      return Promise.resolve(data);
    }
    return readCart().then(function (cart) {
      RMH3.cart.set(cart);
      return cart;
    });
  }

  function hydrateCart() {
    readCart().then(function (cart) {
      RMH3.cart.set(cart);
    }).catch(function () {
      RMH3.cart.set(emptyCart());
    });
  }

  function icon(name, size) {
    var paths = {
      cart: '<circle cx="9" cy="20" r="1.5"/><circle cx="18" cy="20" r="1.5"/><path d="M2 3h3l2.5 13h12L22 7H6"/>',
      check: '<path d="m4 12 5 5 11-12"/>',
      x: '<path d="m6 6 12 12M18 6 6 18"/>',
      trash: '<path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13"/>',
      box: '<rect x="3" y="7" width="18" height="13" rx="1"/><path d="M3 7l9-4 9 4M3 12h18"/>',
      truck: '<path d="M2 7h11v9H2zM13 10h5l3 3v3h-8z"/><circle cx="6" cy="18" r="2"/><circle cx="17" cy="18" r="2"/>',
      search: '<circle cx="11" cy="11" r="7"/><path d="m21 21-4.5-4.5"/>'
    };
    return '<svg class="ic" width="' + (size || 16) + '" height="' + (size || 16) + '" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' + (paths[name] || '') + '</svg>';
  }

  function setButtonState(btn, state) {
    if (!btn) return;
    if (!btn.dataset.defaultHtml) btn.dataset.defaultHtml = btn.innerHTML;
    btn.classList.remove('is-added');
    btn.removeAttribute('aria-busy');
    if (state === 'loading') {
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      btn.innerHTML = '<span class="btn-spinner" aria-hidden="true"></span><span>Adding...</span>';
    } else if (state === 'success') {
      btn.disabled = true;
      btn.classList.add('is-added');
      btn.innerHTML = icon('check', 14) + '<span>Added</span>';
      window.setTimeout(function () { setButtonState(btn, 'idle'); }, 1400);
    } else {
      btn.disabled = false;
      btn.innerHTML = btn.dataset.defaultHtml || btn.innerHTML;
    }
  }

  function addErrorSlot(form) {
    var own = form.querySelector('[data-add-error]');
    if (own) return own;
    var cell = form.closest('td');
    if (cell && cell.querySelector('[data-add-error]')) return cell.querySelector('[data-add-error]');
    var scope = form.closest('.pcard, .pcard-list, .card, aside, .product-form-scope') || form.parentElement;
    return scope && scope.querySelector('[data-add-error]');
  }

  function showAddError(form, error) {
    var slot = addErrorSlot(form);
    if (!slot) return;
    slot.textContent = error.description || error.message || 'Could not add this item. Please try again.';
    slot.hidden = false;
  }

  function clearAddError(form) {
    var slot = addErrorSlot(form);
    if (!slot) return;
    slot.textContent = '';
    slot.hidden = true;
  }

  function formProductMeta(form) {
    var scope = form.closest('.pcard, .pcard-list, .pdp-grid, .card') || document;
    var titleEl = scope.querySelector('.ptitle, h1, [data-product-title]');
    var img = scope.querySelector('img[data-rmh3-product-image], .img-slot img');
    return {
      title: (titleEl && titleEl.textContent.trim()) || 'Item',
      thumb: img ? (img.currentSrc || img.src || '') : '',
      href: (scope.querySelector('a[href]') && scope.querySelector('a[href]').href) || ''
    };
  }

  function openCartDrawer(trigger) {
    var drawer = document.getElementById('cart-drawer');
    if (drawer) openPanel(drawer, trigger);
  }

  function bindAjaxAdd() {
    if (document.__rmh3AjaxAddBound) return;
    document.__rmh3AjaxAddBound = true;
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('form[data-ajax-add]');
      if (!form) return;
      e.preventDefault();
      if (form.__adding) return;
      var id = form.elements.id && form.elements.id.value;
      var qtyField = form.elements.quantity;
      var quantity = Math.max(1, parseInt(qtyField && qtyField.value, 10) || 1);
      var btn = form.querySelector('[type="submit"]');
      if (!id) return;
      form.__adding = true;
      clearAddError(form);
      setButtonState(btn, 'loading');
      cartRequest('/cart/add.js', { items: [{ id: id, quantity: quantity }] })
        .then(setCartFromResponse)
        .then(function () {
          var meta = formProductMeta(form);
          setButtonState(btn, 'success');
          if (form.hasAttribute('data-open-cart')) openCartDrawer(btn);
          RMH3.toast({ title: 'Added to cart', message: meta.title, thumb: meta.thumb, href: meta.href });
        })
        .catch(function (error) {
          setButtonState(btn, 'idle');
          showAddError(form, error);
        })
        .then(function () { form.__adding = false; });
    });
  }

  function cartItemImage(item) {
    var image = item && item.image;
    if (!image) return '';
    if (typeof image === 'string' && image.indexOf('//') === 0) return window.location.protocol + image;
    return image;
  }

  function renderCartRow(item, compact) {
    var image = cartItemImage(item);
    var key = escapeHtml(item.key || item.id);
    var title = escapeHtml(item.product_title || item.title || 'Item');
    var sku = escapeHtml(item.sku || '');
    var vendor = escapeHtml(item.vendor || '');
    var variant = escapeHtml(item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '');
    var rowClass = compact ? 'cart-drawer__line' : '';
    return '<div class="' + rowClass + '" data-cart-line="' + key + '">' +
      '<a class="cart-drawer__thumb img-slot" href="' + escapeHtml(item.url || '/cart') + '">' +
      (image ? '<img src="' + escapeHtml(image) + '" alt="' + title + '" width="96" height="96" loading="lazy" data-rmh3-product-image data-rmh3-api-src="' + escapeHtml(image) + '">' : icon('box', 22)) +
      '</a>' +
      '<div class="cart-drawer__item-main">' +
      '<a class="cart-drawer__title" href="' + escapeHtml(item.url || '/cart') + '">' + title + '</a>' +
      '<div class="cart-drawer__meta">' + [sku, vendor, variant].filter(Boolean).join(' · ') + '</div>' +
      '<div class="row between cart-drawer__buy-row">' +
      '<div class="stepper" data-stepper data-min="1">' +
      '<button type="button" data-dir="down" aria-label="Decrease quantity">−</button>' +
      '<input type="text" inputmode="numeric" value="' + escapeHtml(item.quantity || 1) + '" data-cart-qty-input data-line-key="' + key + '" aria-label="Quantity for ' + title + '">' +
      '<button type="button" data-dir="up" aria-label="Increase quantity">+</button>' +
      '</div>' +
      '<strong class="mono">' + formatMoney(item.final_line_price || item.line_price || 0) + '</strong>' +
      '<button type="button" class="btn btn-xs btn-ghost cart-remove-btn" data-cart-remove data-line-key="' + key + '" aria-label="Remove ' + title + '">' + icon('trash', 14) + '</button>' +
      '</div>' +
      '</div>' +
      '</div>';
  }

  function freeShipInfo(cart, threshold) {
    var subtotal = Number((cart && cart.items_subtotal_price) || (cart && cart.total_price) || 0);
    var target = Number(threshold || 25000);
    var ratio = target > 0 ? Math.min(100, Math.round((subtotal / target) * 100)) : 100;
    if (subtotal >= target) return { text: 'Free shipping unlocked', ok: true, pct: 100 };
    return { text: 'Add ' + formatMoney(target - subtotal) + ' for free shipping', ok: false, pct: ratio };
  }

  function renderDrawer(cart) {
    var drawer = document.getElementById('cart-drawer');
    if (!drawer) return;
    var body = drawer.querySelector('[data-cart-drawer-body]');
    var footer = drawer.querySelector('[data-cart-drawer-footer]');
    var count = drawer.querySelector('[data-cart-drawer-count]');
    var progressText = drawer.querySelector('[data-cart-progress-text]');
    var progressBar = drawer.querySelector('[data-cart-progress-bar]');
    var threshold = parseInt(drawer.getAttribute('data-free-ship-threshold') || '25000', 10);
    cart = cart || emptyCart();
    if (count) count.textContent = cart.item_count + (cart.item_count === 1 ? ' item' : ' items');
    var info = freeShipInfo(cart, threshold);
    if (progressText) {
      progressText.textContent = info.text;
      progressText.classList.toggle('is-unlocked', info.ok);
    }
    if (progressBar) progressBar.style.width = info.pct + '%';
    if (!body || !footer) return;
    if (!cart.items || !cart.items.length) {
      body.innerHTML = '<div class="cart-drawer__empty">' + icon('box', 34) + '<h3>Your cart is empty</h3><p>Browse the catalog and add the supplies you need.</p><a class="btn btn-primary" href="/collections/all">Browse catalog</a></div>';
      footer.innerHTML = '';
    } else {
      body.innerHTML = cart.items.map(function (item) { return renderCartRow(item, true); }).join('');
      var savings = Math.max(0, Number(cart.original_total_price || 0) - Number(cart.total_price || 0));
      footer.innerHTML = '<div class="cart-drawer__summary">' +
        '<div class="row between"><span>Subtotal</span><strong class="mono">' + formatMoney(cart.items_subtotal_price || cart.total_price) + '</strong></div>' +
        (savings > 0 ? '<div class="row between cart-drawer__savings"><span>Savings</span><strong class="mono">-' + formatMoney(savings) + '</strong></div>' : '') +
        '</div>' +
        '<a class="btn btn-primary btn-lg btn-block" href="/checkout">Checkout</a>' +
        '<a class="btn btn-ghost btn-block" href="/cart">View cart</a>';
    }
    bindSteppers(drawer);
    bindProductImages(drawer);
  }

  function updateHeaderCart(cart) {
    cart = cart || emptyCart();
    var count = Number(cart.item_count || 0);
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
      if (lastCartCount != null && count > lastCartCount) {
        el.classList.remove('cart-badge-pop');
        void el.offsetWidth;
        el.classList.add('cart-badge-pop');
      }
    });
    document.querySelectorAll('[data-cart-total]').forEach(function (el) {
      el.textContent = formatMoney(cart.total_price || 0);
    });
    lastCartCount = count;
  }

  function scheduleLineChange(input) {
    var key = input.getAttribute('data-line-key');
    if (!key) return;
    var qty = Math.max(1, parseInt(input.value, 10) || 1);
    input.value = qty;
    window.clearTimeout(cartChangeTimers[key]);
    cartChangeTimers[key] = window.setTimeout(function () {
      cartRequest('/cart/change.js', { id: key, quantity: qty })
        .then(setCartFromResponse)
        .catch(function (error) {
          RMH3.toast({ title: 'Cart update failed', message: error.description || error.message || 'Please try again.' });
          if (RMH3.cart.state) renderDrawer(RMH3.cart.state);
        });
    }, 250);
  }

  function bindCartMutations() {
    if (document.__rmh3CartMutationBound) return;
    document.__rmh3CartMutationBound = true;
    document.addEventListener('change', function (e) {
      var input = e.target.closest('[data-cart-qty-input]');
      if (input) scheduleLineChange(input);
    });
    document.addEventListener('click', function (e) {
      var remove = e.target.closest('[data-cart-remove]');
      if (remove) {
        e.preventDefault();
        var key = remove.getAttribute('data-line-key');
        if (!key) return;
        cartRequest('/cart/change.js', { id: key, quantity: 0 })
          .then(setCartFromResponse)
          .catch(function (error) {
            RMH3.toast({ title: 'Could not remove item', message: error.description || error.message || 'Please try again.' });
          });
      }
      var toastCart = e.target.closest('[data-toast-cart]');
      if (toastCart) {
        e.preventDefault();
        openCartDrawer(toastCart);
      }
    });
    document.addEventListener('submit', function (e) {
      var clearForm = e.target.closest('[data-cart-clear]');
      if (!clearForm) return;
      e.preventDefault();
      var btn = clearForm.querySelector('[type="submit"]');
      setButtonState(btn, 'loading');
      cartRequest('/cart/clear.js', {})
        .then(setCartFromResponse)
        .then(function () {
          setButtonState(btn, 'idle');
          RMH3.toast({ title: 'Cart cleared' });
        })
        .catch(function (error) {
          setButtonState(btn, 'idle');
          RMH3.toast({ title: 'Could not clear cart', message: error.description || error.message || 'Please try again.' });
        });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && activePanel) closePanel(activePanel);
      if (e.key !== 'Tab' || !activePanel) return;
      var nodes = focusableIn(activePanel);
      if (!nodes.length) return;
      var first = nodes[0];
      var last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  }

  function renderCartPage(cart) {
    var page = document.querySelector('[data-cart-page]');
    if (!page) return;
    cart = cart || emptyCart();
    var lines = page.querySelector('[data-cart-page-lines]');
    var count = page.querySelector('[data-cart-page-count]');
    var banner = page.querySelector('[data-cart-page-banner]');
    var threshold = parseInt(page.getAttribute('data-free-ship-threshold') || '25000', 10);
    if (count) count.textContent = cart.item_count + (cart.item_count === 1 ? ' item' : ' items');
    if (banner) {
      var info = freeShipInfo(cart, threshold);
      banner.classList.toggle('success', info.ok);
      banner.classList.toggle('info', !info.ok);
      banner.innerHTML = icon(info.ok ? 'check' : 'truck', 16) + '<span>' + escapeHtml(info.text) + '</span>';
    }
    // The Lot column only exists when the server render found real lot data
    // (data-cart-lot-col on the <th>); mirror that in the JS re-render.
    var hasLot = !!page.querySelector('[data-cart-lot-col]');
    if (lines) {
      if (!cart.items || !cart.items.length) {
        lines.innerHTML = '<tr><td colspan="' + (hasLot ? 8 : 7) + '"><div class="empty"><h4>Your cart is empty</h4><p>Browse the catalog to add supplies.</p><a href="/collections/all" class="btn btn-primary">Browse catalog</a></div></td></tr>';
      } else {
        lines.innerHTML = cart.items.map(function (item) {
          var image = cartItemImage(item);
          var title = escapeHtml(item.product_title || item.title || 'Item');
          var sku = escapeHtml(item.sku || '');
          var vendor = escapeHtml(item.vendor || '');
          var variant = escapeHtml(item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : 'EA');
          var key = escapeHtml(item.key || item.id);
          // cl-* classes are the stable column hooks for the mobile card
          // re-layout in theme.css — keep in sync with main-cart.liquid.
          return '<tr data-cart-page-line="' + key + '">' +
            '<td class="cl-img"><div class="img-slot" style="width:48px;height:48px;border-radius:6px;font-size:9px">' + (image ? '<img src="' + escapeHtml(image) + '" alt="' + title + '" width="48" height="48" loading="lazy" data-rmh3-product-image data-rmh3-api-src="' + escapeHtml(image) + '">' : vendor) + '</div></td>' +
            '<td class="cl-item"><a href="' + escapeHtml(item.url || '/cart') + '"><div style="font-weight:500;font-size:13px">' + title + '</div></a><div class="mono subtle" style="font-size:11px;margin-top:2px">' + sku + ' · ' + vendor + '</div></td>' +
            '<td class="cl-pack subtle" style="font-size:12px">' + variant + '</td>' +
            (hasLot ? '<td class="cl-lot" style="font-size:11px"><div class="mono">—</div></td>' : '') +
            '<td class="cl-qty"><div class="stepper" data-stepper data-min="1"><button type="button" data-dir="down" aria-label="Decrease quantity">−</button><input type="text" inputmode="numeric" value="' + escapeHtml(item.quantity || 1) + '" data-cart-qty-input data-line-key="' + key + '"><button type="button" data-dir="up" aria-label="Increase quantity">+</button></div></td>' +
            '<td class="cl-unit num" style="font-size:13px"><strong>' + formatMoney(item.final_price || item.price || 0) + '</strong></td>' +
            '<td class="cl-total num"><strong>' + formatMoney(item.final_line_price || item.line_price || 0) + '</strong></td>' +
            '<td class="cl-remove"><button type="button" class="btn btn-xs btn-ghost" data-cart-remove data-line-key="' + key + '" aria-label="Remove ' + title + '">' + icon('x', 14) + '</button></td>' +
          '</tr>';
        }).join('');
      }
      bindSteppers(lines);
      bindProductImages(lines);
    }
    var savings = Math.max(0, Number(cart.original_total_price || 0) - Number(cart.total_price || 0));
    var subtotal = page.querySelector('[data-cart-page-subtotal]');
    var discount = page.querySelector('[data-cart-page-discount]');
    var total = page.querySelector('[data-cart-page-total]');
    var saved = page.querySelector('[data-cart-page-saved]');
    var savingsRow = page.querySelector('[data-cart-page-savings-row]');
    var savedRow = page.querySelector('[data-cart-page-saved-row]');
    if (subtotal) subtotal.textContent = formatMoney(cart.items_subtotal_price || cart.total_price || 0);
    if (discount) discount.textContent = '-' + formatMoney(savings);
    if (total) total.textContent = formatMoney(cart.total_price || 0);
    if (saved) saved.textContent = formatMoney(savings);
    if (savingsRow) savingsRow.hidden = savings <= 0;
    if (savedRow) savedRow.hidden = savings <= 0;
  }

  function bindSkuAdd() {
    if (document.__rmh3SkuAddBound) return;
    document.__rmh3SkuAddBound = true;
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('[data-add-by-sku]');
      if (!form) return;
      e.preventDefault();
      var query = (form.querySelector('[name="q"]') || {}).value || '';
      var qty = Math.max(1, parseInt((form.querySelector('[name="quantity"]') || {}).value, 10) || 1);
      var btn = form.querySelector('[type="submit"]');
      clearAddError(form);
      if (!query.trim()) {
        showAddError(form, { description: 'Enter a SKU first.' });
        return;
      }
      setButtonState(btn, 'loading');
      findVariantBySku(query.trim())
        .then(function (variant) {
          if (!variant) throw { description: 'No matching SKU was found.' };
          return cartRequest('/cart/add.js', { items: [{ id: variant.id, quantity: qty }] });
        })
        .then(setCartFromResponse)
        .then(function () {
          setButtonState(btn, 'success');
          if (form.hasAttribute('data-open-cart')) openCartDrawer(btn);
          RMH3.toast({ title: 'Added to cart', message: query.trim() });
          form.reset();
        })
        .catch(function (error) {
          setButtonState(btn, 'idle');
          showAddError(form, error);
        });
    });
  }

  function normalizeItemCode(value) {
    // LOCKED matching rule: strip DASHES only. Never strip other punctuation
    // and never touch the digit sequence itself (no leading-zero trims, no
    // whitespace collapsing inside codes) — "0461453" and "046-1453" are the
    // same item code; nothing else is.
    return String(value || '').trim().toLowerCase().replace(/-/g, '');
  }

  function findVariantBySku(sku) {
    // Resolve a store item code to a variant. Matching is dash-INSENSITIVE
    // on both sides (normalizeItemCode) — lab techs type item codes without
    // dashes ("0461453" for 046-1453). The predictive-search engine itself
    // tokenizes on dashes and returns NOTHING for the dash-less form, so
    // after the as-typed query we retry dash-normalized alternates: dashes
    // stripped, and — for a 7-digit dash-less input — the canonical NNN-NNNN
    // house item-code shape.
    var raw = String(sku || '').trim().toLowerCase();
    if (!raw) return Promise.resolve(null);
    var want = normalizeItemCode(raw);
    var attempts = [raw];
    var stripped = raw.replace(/-/g, '');
    if (stripped !== raw) attempts.push(stripped);
    if (/^\d{7}$/.test(stripped)) {
      var canonical = stripped.slice(0, 3) + '-' + stripped.slice(3);
      if (attempts.indexOf(canonical) === -1) attempts.push(canonical);
    }
    return attempts.reduce(function (chain, term) {
      return chain.then(function (found) { return found || suggestVariantLookup(term, want); });
    }, Promise.resolve(null));
  }

  function suggestVariantLookup(term, want) {
    // One predictive-search attempt, same pattern as the header typeahead:
    // match variants.sku AND the mhlhub-sku: tag, then fetch
    // /products/{handle}.js for the variant. All SKU/tag comparisons go
    // through normalizeItemCode so a dashed store code matches a dash-less
    // query and vice versa. (Predictive-search product.url carries ?_pos=…
    // tracking params, so the old `product.url + '.js'` always 404'd — never
    // build URLs from it.)
    var url = cartEndpoint('search/suggest.json') + '?q=' + encodeURIComponent(term) +
      '&resources[type]=product&resources[limit]=8' +
      '&resources[options][unavailable_products]=last' +
      '&resources[options][fields]=' + encodeURIComponent('variants.sku,variants.barcode,tag,title');
    return fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : Promise.reject({ description: 'Could not search by SKU.' }); })
      .then(function (data) {
        var products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
        // Products whose master item code (mhlhub-sku: tag) matches go first.
        var tagged = [];
        var rest = [];
        products.forEach(function (p) {
          var tags = p.tags || [];
          var hit = false;
          for (var i = 0; i < tags.length; i++) {
            var t = String(tags[i]);
            if (t.indexOf('mhlhub-sku:') === 0 && normalizeItemCode(t.slice(11)) === want) { hit = true; break; }
          }
          (hit ? tagged : rest).push(p);
        });
        return tagged.concat(rest).reduce(function (chain, product) {
          return chain.then(function (found) {
            if (found) return found;
            var handle = product.handle || String(product.url || '').split('?')[0].split('/').pop();
            if (!handle) return null;
            return fetch(cartEndpoint('products/' + handle + '.js'), { credentials: 'same-origin', headers: { Accept: 'application/json' } })
              .then(function (res) { return res.ok ? res.json() : null; })
              .then(function (p) {
                if (!p) return null;
                var variants = p.variants || [];
                var v = variants.find(function (x) { return normalizeItemCode(x.sku) === want; }) || null;
                // Single-variant product matched by master item-code tag: trust the tag.
                if (!v && variants.length === 1 && tagged.indexOf(product) !== -1) v = variants[0];
                if (v && !v.product_title) v.product_title = p.title || product.title || '';
                return v;
              });
          });
        }, Promise.resolve(null));
      });
  }

  // --- Cart tools: bulk SKU import + saved lists (cart page) -----------------
  // Import/Bulk upload: paste or upload "SKU, qty" lines, resolve each through
  // findVariantBySku, batch-add, and report per line. Saved lists: named
  // localStorage lists with a load-into-cart path (browser-local by design —
  // customer-account-backed lists are a future feature).
  var CART_LISTS_KEY = 'rmh3.cart.lists';

  function readCartLists() {
    try {
      var parsed = JSON.parse(window.localStorage.getItem(CART_LISTS_KEY) || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) { return []; }
  }

  function writeCartLists(lists) {
    try { window.localStorage.setItem(CART_LISTS_KEY, JSON.stringify(lists)); return true; }
    catch (e) { return false; }
  }

  function parseSkuLines(text) {
    var rows = [];
    var byKey = {};
    String(text || '').split(/\r?\n/).forEach(function (line) {
      var t = line.trim();
      if (!t) return;
      var parts = t.split(/[,\t;]+|\s+/).filter(Boolean);
      if (!parts.length) return;
      var sku = parts[0].trim();
      if (!sku || /^(sku|item|item[-_ ]?code|code|qty|quantity)$/i.test(sku)) return; // header row
      var qty = 1;
      if (parts[1] != null) {
        var q = parseInt(String(parts[1]).replace(/[^0-9]/g, ''), 10);
        if (Number.isFinite(q) && q > 0) qty = Math.min(q, 9999);
      }
      // Dash-insensitive dedup (normalizeItemCode): "046-1453" and "0461453"
      // on separate lines are the same item code — merge their quantities.
      var k = normalizeItemCode(sku);
      if (byKey[k]) byKey[k].qty = Math.min(byKey[k].qty + qty, 9999);
      else { byKey[k] = { sku: sku, qty: qty }; rows.push(byKey[k]); }
    });
    return rows;
  }

  function importReportLine(ok, text) {
    return '<li class="' + (ok ? 'ok' : 'fail') + '">' + icon(ok ? 'check' : 'x', 13) + '<span>' + escapeHtml(text) + '</span></li>';
  }

  function runSkuImport(modal) {
    var textarea = modal.querySelector('[data-import-text]');
    var runBtn = modal.querySelector('[data-import-run]');
    var status = modal.querySelector('[data-import-status]');
    var report = modal.querySelector('[data-import-report]');
    if (!textarea || modal.__importing) return;
    var rows = parseSkuLines(textarea.value);
    function say(msg) { if (status) { status.textContent = msg; status.hidden = !msg; } }
    if (report) { report.innerHTML = ''; report.hidden = true; }
    if (!rows.length) { say('Nothing to import — paste one SKU per line first.'); return; }
    modal.__importing = true;
    if (runBtn) setButtonState(runBtn, 'loading');
    var results = [];
    var chain = Promise.resolve();
    rows.forEach(function (row, i) {
      chain = chain.then(function () {
        say('Looking up ' + (i + 1) + ' of ' + rows.length + '… (' + row.sku + ')');
        return findVariantBySku(row.sku)
          .catch(function () { return null; })
          .then(function (variant) { results.push({ row: row, variant: variant, ok: false, error: '' }); });
      });
    });
    chain.then(function () {
      var resolved = results.filter(function (r) { return r.variant; });
      if (!resolved.length) return null;
      say('Adding ' + resolved.length + ' item' + (resolved.length === 1 ? '' : 's') + ' to cart…');
      var items = resolved.map(function (r) { return { id: r.variant.id, quantity: r.row.qty }; });
      return cartRequest('/cart/add.js', { items: items })
        .then(function (data) {
          resolved.forEach(function (r) { r.ok = true; });
          return data;
        })
        .catch(function () {
          // Batch add rejects the whole request if any line fails (e.g. sold
          // out) — retry one by one so the report is honest per line.
          var single = Promise.resolve(null);
          resolved.forEach(function (r) {
            single = single.then(function (last) {
              return cartRequest('/cart/add.js', { items: [{ id: r.variant.id, quantity: r.row.qty }] })
                .then(function (data) { r.ok = true; return data; })
                .catch(function (error) {
                  r.error = error.description || error.message || 'Could not add.';
                  return last;
                });
            });
          });
          return single;
        });
    }).then(function (lastCartResponse) {
      if (lastCartResponse) setCartFromResponse(lastCartResponse);
      var added = results.filter(function (r) { return r.ok; }).length;
      say('');
      if (report) {
        report.innerHTML = results.map(function (r) {
          if (r.ok) return importReportLine(true, r.row.sku + ' ×' + r.row.qty + (r.variant.product_title ? ' — ' + r.variant.product_title : ''));
          if (!r.variant) return importReportLine(false, r.row.sku + ' — no matching SKU in the catalog');
          return importReportLine(false, r.row.sku + ' — ' + (r.error || 'could not add'));
        }).join('');
        report.hidden = false;
      }
      RMH3.toast({ title: 'SKU import', message: added + ' of ' + rows.length + ' line' + (rows.length === 1 ? '' : 's') + ' added to cart' });
    }).then(function () {
      modal.__importing = false;
      if (runBtn) setButtonState(runBtn, 'idle');
    });
  }

  function renderCartLists(modal) {
    var rowsEl = modal.querySelector('[data-lists-rows]');
    if (!rowsEl) return;
    var lists = readCartLists();
    if (!lists.length) {
      rowsEl.innerHTML = '<div class="cart-list-empty subtle">No saved lists yet. Save your current cart to reorder it in one click.</div>';
      return;
    }
    rowsEl.innerHTML = lists.map(function (list, i) {
      var count = (list.items || []).reduce(function (n, it) { return n + (Number(it.quantity) || 0); }, 0);
      var when = list.savedAt ? new Date(list.savedAt).toLocaleDateString() : '';
      return '<div class="cart-list-row">' +
        '<div class="cart-list-row__main"><strong>' + escapeHtml(list.name || 'Saved list') + '</strong>' +
        '<small class="subtle">' + count + ' item' + (count === 1 ? '' : 's') + ((list.items || []).length ? ' · ' + list.items.length + ' SKU' + (list.items.length === 1 ? '' : 's') : '') + (when ? ' · ' + when : '') + '</small></div>' +
        '<button type="button" class="btn btn-sm btn-primary" data-list-load="' + i + '">Add to cart</button>' +
        '<button type="button" class="btn btn-sm btn-ghost" data-list-delete="' + i + '" aria-label="Delete list ' + escapeHtml(list.name || '') + '">' + icon('trash', 14) + '</button>' +
        '</div>';
    }).join('');
  }

  function bindCartTools() {
    if (document.__rmh3CartToolsBound) return;
    document.__rmh3CartToolsBound = true;

    document.addEventListener('click', function (e) {
      var openImport = e.target.closest('[data-cart-import-open]');
      if (openImport) {
        e.preventDefault();
        var importModal = document.getElementById('cart-import-modal');
        if (importModal) openPanel(importModal, openImport);
        return;
      }
      var openLists = e.target.closest('[data-cart-lists-open]');
      if (openLists) {
        e.preventDefault();
        var listsModal = document.getElementById('cart-lists-modal');
        if (listsModal) { renderCartLists(listsModal); openPanel(listsModal, openLists); }
        return;
      }
      var runBtn = e.target.closest('[data-import-run]');
      if (runBtn) {
        var m = runBtn.closest('[data-import-modal]');
        if (m) runSkuImport(m);
        return;
      }
      var load = e.target.closest('[data-list-load]');
      if (load) {
        var loadModal = load.closest('[data-lists-modal]');
        var list = readCartLists()[parseInt(load.getAttribute('data-list-load'), 10)];
        var items = list && (list.items || []).filter(function (it) { return it.id && Number(it.quantity) > 0; })
          .map(function (it) { return { id: it.id, quantity: Number(it.quantity) }; });
        if (!items || !items.length) return;
        setButtonState(load, 'loading');
        cartRequest('/cart/add.js', { items: items })
          .then(setCartFromResponse)
          .then(function () {
            setButtonState(load, 'success');
            RMH3.toast({ title: 'List added to cart', message: list.name || '' });
          })
          .catch(function (error) {
            setButtonState(load, 'idle');
            var status = loadModal && loadModal.querySelector('[data-lists-status]');
            if (status) { status.textContent = error.description || error.message || 'Could not add this list — some items may be unavailable.'; status.hidden = false; }
          });
        return;
      }
      var del = e.target.closest('[data-list-delete]');
      if (del) {
        var lists = readCartLists();
        lists.splice(parseInt(del.getAttribute('data-list-delete'), 10), 1);
        writeCartLists(lists);
        var delModal = del.closest('[data-lists-modal]');
        if (delModal) renderCartLists(delModal);
        return;
      }
    });

    document.addEventListener('submit', function (e) {
      var form = e.target.closest('[data-list-save-form]');
      if (!form) return;
      e.preventDefault();
      var modal = form.closest('[data-lists-modal]');
      var nameInput = form.querySelector('[data-list-name]');
      var status = modal && modal.querySelector('[data-lists-status]');
      function say(msg) { if (status) { status.textContent = msg; status.hidden = !msg; } }
      var cart = RMH3.cart.state;
      if (!cart || !cart.items || !cart.items.length) { say('Your cart is empty — add items before saving a list.'); return; }
      var name = (nameInput && nameInput.value.trim()) || ('Cart ' + new Date().toISOString().slice(0, 10));
      var lists = readCartLists();
      lists.unshift({
        name: name,
        savedAt: new Date().toISOString(),
        items: cart.items.map(function (it) {
          return { id: it.variant_id || it.id, quantity: it.quantity, sku: it.sku || '', title: it.product_title || it.title || '' };
        })
      });
      if (!writeCartLists(lists)) { say('Could not save — browser storage is unavailable.'); return; }
      if (nameInput) nameInput.value = '';
      say('');
      if (modal) renderCartLists(modal);
      RMH3.toast({ title: 'List saved', message: name });
    });

    document.addEventListener('change', function (e) {
      var fileInput = e.target.closest('[data-import-file]');
      if (!fileInput || !fileInput.files || !fileInput.files[0]) return;
      var modal = fileInput.closest('[data-import-modal]');
      var textarea = modal && modal.querySelector('[data-import-text]');
      if (!textarea) return;
      var reader = new FileReader();
      reader.onload = function () {
        var text = String(reader.result || '').trim();
        textarea.value = textarea.value.trim() ? textarea.value.replace(/\s+$/, '') + '\n' + text : text;
        fileInput.value = '';
      };
      reader.readAsText(fileInput.files[0]);
    });
  }

  // Search typeahead — debounced (150ms) predictive search on the header /
  // hero search boxes. Renders query suggestions above product rows (thumb
  // with monogram fallback, title, SKU, price, inline Add via /cart/add.js),
  // full keyboard nav (arrows/Enter/Escape) with combobox semantics. Enter
  // with no highlighted option submits the form to /search natively — unless
  // the typed string exactly matches a product SKU (mhlhub-sku tag), which
  // jumps straight to that PDP.
  // pop:N popularity tiers, tagged on ~1,057 products (pop:1 = top 100 by
  // blended own-sales + Net32 demand, pop:2 next 400, pop:3 next ~580).
  // /search/suggest.json returns the FULL product tags array (verified — the
  // mhlhub-sku pin already relies on it), so the dropdown floats by tier
  // directly from tags; no static top-100 asset needed.
  function popTier(product) {
    var tags = (product && product.tags) || [];
    for (var i = 0; i < tags.length; i++) {
      var t = String(tags[i]);
      if (t === 'pop:1') return 1;
      if (t === 'pop:2') return 2;
      if (t === 'pop:3') return 3;
    }
    return 4;
  }

  function floatByPopTier(products) {
    // Stable float: tier ascending, original (relevance) order within a tier.
    return products
      .map(function (p, i) { return { p: p, i: i, t: popTier(p) }; })
      .sort(function (a, b) { return a.t - b.t || a.i - b.i; })
      .map(function (x) { return x.p; });
  }

  // Zero-result query expansion map (buyer/competitor vocabulary → ours),
  // generated from data/search-term-corpus.json mappedQuery entries into
  // assets/rmh3-search-rewrites.json — the asset refreshes whenever the
  // search-wiring task re-runs. Keys are normalized with normalizeItemCode
  // (lowercase/trim + dash-strip ONLY, per the locked matching rule).
  // theme.liquid exposes the asset URL as window.RMH3_SEARCH_REWRITES_URL.
  var searchRewritesPromise = null;
  function loadSearchRewrites() {
    if (!window.RMH3_SEARCH_REWRITES_URL) return Promise.resolve(null);
    if (!searchRewritesPromise) {
      searchRewritesPromise = fetch(window.RMH3_SEARCH_REWRITES_URL, { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }
    return searchRewritesPromise;
  }

  var searchPanelSeq = 0;
  function bindSearchTypeahead(root) {
    (root || document).querySelectorAll('form.search-wrap[role="search"], form.home-v0-search[role="search"]').forEach(function (form) {
      if (form.__boundSearchTypeahead) return;
      form.__boundSearchTypeahead = true;
      var input = form.querySelector('input[type="search"]');
      if (!input) return;
      searchPanelSeq += 1;
      var panelId = 'search-suggestions-' + searchPanelSeq;
      var panel = document.createElement('div');
      panel.className = 'search-suggestions';
      panel.id = panelId;
      panel.setAttribute('role', 'listbox');
      panel.hidden = true;
      form.appendChild(panel);
      input.setAttribute('role', 'combobox');
      input.setAttribute('aria-autocomplete', 'list');
      input.setAttribute('aria-expanded', 'false');
      input.setAttribute('aria-controls', panelId);
      var timer = null;
      var lastQuery = '';
      var controller = null;
      var skuMatchUrl = '';
      var activeIndex = -1;

      function setExpanded(on) {
        input.setAttribute('aria-expanded', on ? 'true' : 'false');
        if (!on) input.removeAttribute('aria-activedescendant');
      }

      function hide() {
        panel.hidden = true;
        panel.innerHTML = '';
        activeIndex = -1;
        skuMatchUrl = '';
        setExpanded(false);
      }

      function options() {
        return Array.prototype.slice.call(panel.querySelectorAll('[role="option"]'));
      }

      function setActive(idx) {
        var opts = options();
        if (!opts.length) return;
        if (idx < 0) idx = opts.length - 1;
        if (idx >= opts.length) idx = 0;
        activeIndex = idx;
        opts.forEach(function (o, i) {
          o.classList.toggle('is-active', i === idx);
          o.setAttribute('aria-selected', i === idx ? 'true' : 'false');
        });
        input.setAttribute('aria-activedescendant', opts[idx].id);
        if (opts[idx].scrollIntoView) opts[idx].scrollIntoView({ block: 'nearest' });
      }

      function productSku(product) {
        var tags = product.tags || [];
        for (var i = 0; i < tags.length; i++) {
          if (String(tags[i]).indexOf('mhlhub-sku:') === 0) return String(tags[i]).slice(11);
        }
        return '';
      }

      function productImg(product) {
        var img = product.featured_image;
        if (img && typeof img === 'object') img = img.url || '';
        return img || '';
      }

      function productRow(product, i) {
        var img = productImg(product);
        var sku = productSku(product);
        var priceNum = parseFloat(product.price);
        var por = !(priceNum > 0);
        var initial = String(product.vendor || product.title || '').trim().charAt(0).toUpperCase();
        var priceHtml = por ? '<em>Quote</em>' : '$' + escapeHtml(product.price);
        var canAdd = !por && product.available !== false && product.handle;
        return '<div class="search-suggestion" role="option" id="' + panelId + '-opt-p' + i + '" aria-selected="false" data-suggest-url="' + escapeHtml(product.url || '') + '">' +
          '<a class="search-suggestion__main" href="' + escapeHtml(product.url || '#') + '" tabindex="-1">' +
            '<span class="search-suggestion__thumb img-slot">' + (img ? '<img src="' + escapeHtml(img) + '" alt="" width="44" height="44" loading="lazy">' : '<span class="img-ph img-ph--xs" aria-hidden="true"><b>' + escapeHtml(initial) + '</b></span>') + '</span>' +
            '<span class="search-suggestion__body"><strong>' + escapeHtml(product.title || 'Product') + '</strong><small>' + escapeHtml([sku, product.vendor].filter(Boolean).join(' · ')) + '</small></span>' +
            '<span class="search-suggestion__price tnum">' + priceHtml + '</span>' +
          '</a>' +
          (canAdd ? '<button type="button" class="btn btn-xs btn-primary search-suggestion__add" data-suggest-add data-suggest-handle="' + escapeHtml(product.handle) + '" aria-label="Add ' + escapeHtml(product.title || 'product') + ' to cart">Add</button>' : '') +
          '</div>';
      }

      function queryRow(qr, i) {
        var text = qr.styled_text || escapeHtml(qr.text || '');
        return '<a class="search-suggestion__query" role="option" id="' + panelId + '-opt-q' + i + '" aria-selected="false" href="' + escapeHtml(qr.url) + '" data-suggest-url="' + escapeHtml(qr.url) + '" tabindex="-1">' + icon('search', 13) + '<span>' + text + '</span></a>';
      }

      function render(query, results, rewrittenFrom) {
        // Stable pop-tier float (pop:1 → pop:2 → pop:3 → untagged); the
        // exact-SKU Enter-jump scan below is order-independent.
        var products = floatByPopTier(results.products || []);
        var queries = (results.queries || []).slice(0, 3);
        skuMatchUrl = '';
        var qlc = query.toLowerCase();
        for (var i = 0; i < products.length; i++) {
          var s = productSku(products[i]);
          if (s && s.toLowerCase() === qlc && products[i].url) { skuMatchUrl = products[i].url; break; }
        }
        if (!products.length && !queries.length) {
          panel.innerHTML = '<div class="search-suggestions__empty">No quick matches. Press Enter to search the full catalog.</div>';
        } else {
          var html = '';
          if (rewrittenFrom) {
            html += '<div class="search-suggestions__empty" style="border-bottom:1px solid var(--border)">Showing results for “<strong>' + escapeHtml(query) + '</strong>”. <a href="' + cartEndpoint('search') + '?q=' + encodeURIComponent(rewrittenFrom) + '&rmh3_rw=0">Search instead for “' + escapeHtml(rewrittenFrom) + '”</a></div>';
          }
          if (queries.length) html += '<div class="search-suggestions__group">' + queries.map(queryRow).join('') + '</div>';
          if (products.length) html += '<div class="search-suggestions__head">Products</div>' + products.map(productRow).join('');
          var allUrl = cartEndpoint('search') + '?q=' + encodeURIComponent(query);
          html += '<a class="search-suggestions__all" role="option" id="' + panelId + '-opt-all" aria-selected="false" href="' + allUrl + '" data-suggest-url="' + allUrl + '" tabindex="-1">See all results for “' + escapeHtml(query) + '”</a>';
          panel.innerHTML = html;
        }
        panel.hidden = false;
        activeIndex = -1;
        setExpanded(true);
      }

      function search(query, rewrittenFrom) {
        // rewrittenFrom = original zero-result term when this call is the
        // auto-retry with the corpus-mapped query (see loadSearchRewrites).
        if (!rewrittenFrom) {
          if (query.length < 2) { lastQuery = query; hide(); return; }
          if (query === lastQuery && !panel.hidden) return;
          lastQuery = query;
        }
        if (controller) controller.abort();
        controller = window.AbortController ? new AbortController() : null;
        var url = cartEndpoint('search/suggest.json') + '?q=' + encodeURIComponent(query) +
          '&resources[type]=product,query&resources[limit]=6&resources[limit_scope]=each' +
          '&resources[options][unavailable_products]=last' +
          '&resources[options][fields]=' + encodeURIComponent('title,product_type,variants.title,variants.sku,variants.barcode,vendor,tag');
        fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: controller ? controller.signal : undefined })
          .then(function (res) { return res.ok ? res.json() : null; })
          .then(function (data) {
            if (!data) return;
            var results = (data.resources && data.resources.results) || {};
            // The input still holds the ORIGINAL text during a rewritten
            // retry — guard against the user having typed on since.
            var current = input.value.trim();
            if (rewrittenFrom ? current !== rewrittenFrom : current !== query) return;
            var noHits = !((results.products || []).length) && !((results.queries || []).length);
            if (noHits && !rewrittenFrom) {
              // Zero-result expansion: competitor vocab → ours. Only when
              // nothing matched; never rewrites a query that has results.
              loadSearchRewrites().then(function (map) {
                var mapped = map && map[normalizeItemCode(query)];
                if (mapped && normalizeItemCode(mapped) !== normalizeItemCode(query) && input.value.trim() === query) {
                  search(mapped, query);
                } else if (input.value.trim() === query) {
                  render(query, results);
                }
              });
              return;
            }
            render(query, results, rewrittenFrom);
          })
          .catch(function (err) { if (!err || err.name !== 'AbortError') hide(); });
      }

      panel.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-suggest-add]');
        if (btn) {
          e.preventDefault();
          e.stopPropagation();
          if (btn.__adding) return;
          btn.__adding = true;
          setButtonState(btn, 'loading');
          var handle = btn.getAttribute('data-suggest-handle');
          fetch(cartEndpoint('products/' + handle + '.js'), { credentials: 'same-origin', headers: { Accept: 'application/json' } })
            .then(function (res) { return res.ok ? res.json() : Promise.reject({}); })
            .then(function (prod) {
              var variants = (prod && prod.variants) || [];
              var v = null;
              for (var i = 0; i < variants.length; i++) { if (variants[i].available) { v = variants[i]; break; } }
              v = v || variants[0];
              if (!v) throw {};
              return cartRequest('/cart/add.js', { items: [{ id: v.id, quantity: 1 }] });
            })
            .then(setCartFromResponse)
            .then(function () {
              setButtonState(btn, 'success');
              var row = btn.closest('.search-suggestion');
              var titleEl = row && row.querySelector('.search-suggestion__body strong');
              RMH3.toast({ title: 'Added to cart', message: titleEl ? titleEl.textContent : '' });
            })
            .catch(function () { setButtonState(btn, 'idle'); })
            .then(function () { btn.__adding = false; });
          return;
        }
        var opt = e.target.closest('[role="option"]');
        if (opt && opt.getAttribute('data-suggest-url') && !e.target.closest('a')) {
          window.location.href = opt.getAttribute('data-suggest-url');
        }
      });

      input.addEventListener('input', function () {
        var query = input.value.trim();
        window.clearTimeout(timer);
        timer = window.setTimeout(function () { search(query); }, 150);
      });
      input.addEventListener('focus', function () {
        var query = input.value.trim();
        if (query.length >= 2) search(query);
      });
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { hide(); return; }
        if (panel.hidden) return;
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setActive(activeIndex + 1);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setActive(activeIndex - 1);
        } else if (e.key === 'Enter') {
          var opts = options();
          if (activeIndex >= 0 && opts[activeIndex]) {
            e.preventDefault();
            var target = opts[activeIndex].getAttribute('data-suggest-url');
            if (target) window.location.href = target;
          } else if (skuMatchUrl) {
            e.preventDefault();
            window.location.href = skuMatchUrl;
          }
          // otherwise fall through: native form submit lands on /search?q=…
        }
      });
      document.addEventListener('click', function (e) {
        if (!form.contains(e.target)) hide();
      });
    });
  }

  RMH3.toast = function (opts) {
    opts = opts || {};
    var region = document.querySelector('[data-toast-region]');
    if (!region) {
      region = document.createElement('div');
      region.className = 'toast-region';
      region.setAttribute('data-toast-region', '');
      region.setAttribute('aria-live', 'polite');
      document.body.appendChild(region);
    }
    var toast = document.createElement('div');
    toast.className = 'toast';
    var thumb = opts.thumb ? '<img src="' + escapeHtml(opts.thumb) + '" alt="" width="44" height="44" loading="lazy" data-rmh3-product-image data-rmh3-api-src="' + escapeHtml(opts.thumb) + '">' : icon('cart', 22);
    toast.innerHTML = '<div class="toast__thumb">' + thumb + '</div><div class="toast__body"><strong>' + escapeHtml(opts.title || 'Added to cart') + '</strong>' + (opts.message ? '<p>' + escapeHtml(opts.message) + '</p>' : '') + '<button type="button" class="toast__link" data-toast-cart>View cart</button></div><button type="button" class="toast__close" aria-label="Dismiss">' + icon('x', 14) + '</button>';
    region.appendChild(toast);
    bindProductImages(toast);
    toast.querySelector('.toast__close').addEventListener('click', function () { toast.remove(); });
    window.setTimeout(function () { toast.classList.add('is-hiding'); window.setTimeout(function () { toast.remove(); }, 220); }, 3500);
  };

  // Best-sellers carousel — horizontal scroll-snap track with auto-advance,
  // pause-on-hover/focus, prev/next, and dots. Mirrors the hero slideshow's
  // setInterval pattern. Degrades to a static centered row when the cards
  // fit (few products): no scrolling, arrows + dots stay hidden.
  function bindBestCarousel(root) {
    (root || document).querySelectorAll('[data-best-carousel]').forEach(function (el) {
      if (el.__bestCarouselBound) return;
      el.__bestCarouselBound = true;

      var viewport = el.querySelector('[data-carousel-viewport]');
      var track = el.querySelector('[data-carousel-track]');
      var prev = el.querySelector('[data-carousel-prev]');
      var next = el.querySelector('[data-carousel-next]');
      var dotsWrap = el.querySelector('[data-carousel-dots]');
      if (!viewport || !track) return;

      var cards = Array.prototype.slice.call(track.querySelectorAll('.home-v0-best-card'));
      var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      // Auto-rotate disabled 2026-08-03: unprompted motion on a task-focused
      // B2B catalog read as un-premium, and the timer was what lurched the
      // rail to its clipped end-state every few seconds. Arrows / dots /
      // manual scroll still drive it; the rail rests flush at the first card.
      // Flip AUTO_ROTATE back to true to re-enable.
      var AUTO_ROTATE = false;
      var AUTO_MS = 4500;
      var timer = null;
      var page = 0;

      // A "page" advances by ~one screenful, but every INTERMEDIATE resting
      // position is a CARD boundary (left edge = a whole card); the final page
      // rests flush-right at maxScroll (last cards fully shown). The old model
      // scrolled to idx*viewportWidth, whose last step landed mid-card — the
      // sliced first-card the auto-rotate lurched onto every 4.5s.
      function pageWidth() { return viewport.clientWidth; }
      function maxScroll() { return Math.max(0, track.scrollWidth - viewport.clientWidth); }
      function pageStarts() {
        var ms = maxScroll();
        if (ms <= 1 || !cards.length) return [0];
        var pw = pageWidth();
        var starts = [0];
        while (true) {
          var cur = starts[starts.length - 1];
          if (cur >= ms - 1) break;
          var nextStart = null;
          for (var k = 0; k < cards.length; k++) {
            if (cards[k].offsetLeft >= cur + pw - 1) { nextStart = cards[k].offsetLeft; break; }
          }
          if (nextStart === null || nextStart >= ms) { starts.push(ms); break; }
          starts.push(nextStart);
        }
        return starts.filter(function (v, idx, a) { return a.indexOf(v) === idx; });
      }
      function pageCount() { return pageStarts().length; }

      function scrollable() { return maxScroll() > 2 && cards.length > 0; }

      function buildDots() {
        if (!dotsWrap) return;
        dotsWrap.innerHTML = '';
        var n = pageCount();
        for (var i = 0; i < n; i++) {
          var b = document.createElement('button');
          b.type = 'button';
          b.setAttribute('aria-label', 'Go to slide ' + (i + 1));
          b.dataset.page = String(i);
          b.addEventListener('click', (function (idx) {
            return function () { goTo(idx); resetTimer(); };
          })(i));
          dotsWrap.appendChild(b);
        }
      }

      function currentPage() {
        var starts = pageStarts();
        var x = viewport.scrollLeft;
        var best = 0, bestD = Infinity;
        for (var i = 0; i < starts.length; i++) {
          var d = Math.abs(starts[i] - x);
          if (d < bestD) { bestD = d; best = i; }
        }
        return best;
      }

      function syncControls() {
        var on = scrollable();
        el.classList.toggle('is-scrollable', on);
        if (prev) prev.hidden = !on;
        if (next) next.hidden = !on;
        if (dotsWrap) dotsWrap.hidden = !on || pageCount() < 2;
        if (!on) { stop(); return; }

        page = currentPage();
        var atStart = page <= 0;
        var atEnd = page >= pageCount() - 1;
        // Hide (not just dim) the dead control at each end — no ghost arrow
        // sitting over the flush first/last card.
        if (prev) { prev.hidden = atStart; prev.disabled = atStart; }
        if (next) { next.hidden = atEnd; next.disabled = atEnd; }
        if (dotsWrap) {
          dotsWrap.querySelectorAll('button').forEach(function (d, i) {
            d.classList.toggle('active', i === page);
          });
        }
      }

      function goTo(idx) {
        var starts = pageStarts();
        var n = starts.length;
        if (n <= 1) return;
        idx = ((idx % n) + n) % n;
        viewport.scrollTo({ left: starts[idx], behavior: reduceMotion ? 'auto' : 'smooth' });
      }

      function nextPage() {
        var n = pageCount();
        var i = currentPage();
        goTo(i + 1 >= n ? 0 : i + 1); // loop back to start
      }
      function prevPage() { goTo(currentPage() - 1); }

      function start() {
        if (!AUTO_ROTATE || reduceMotion || !scrollable() || pageCount() < 2) return;
        stop();
        timer = window.setInterval(nextPage, AUTO_MS);
      }
      function stop() { if (timer) { window.clearInterval(timer); timer = null; } }
      function resetTimer() { stop(); start(); }

      if (prev) prev.addEventListener('click', function () { prevPage(); resetTimer(); });
      if (next) next.addEventListener('click', function () { nextPage(); resetTimer(); });

      // Pause on hover / focus-within; resume on leave / blur.
      el.addEventListener('mouseenter', stop);
      el.addEventListener('mouseleave', start);
      el.addEventListener('focusin', stop);
      el.addEventListener('focusout', start);

      var scrollRAF = null;
      viewport.addEventListener('scroll', function () {
        if (scrollRAF) return;
        scrollRAF = window.requestAnimationFrame(function () {
          scrollRAF = null;
          syncControls();
        });
      });

      buildDots();
      syncControls();
      start();

      var resizeT = null;
      window.addEventListener('resize', function () {
        window.clearTimeout(resizeT);
        resizeT = window.setTimeout(function () {
          buildDots();
          syncControls();
          resetTimer();
        }, 150);
      });
    });
  }

  function bindPdpEnhancements(root) {
    (root || document).querySelectorAll('[data-pdp-product]').forEach(function (pdp) {
      if (pdp.__pdpEnhancementsBound) return;
      pdp.__pdpEnhancementsBound = true;

      var unitEl = pdp.querySelector('[data-pdp-unit-price]');
      var unitPrice = parseInt(unitEl && unitEl.getAttribute('data-pdp-unit-price'), 10) || 0;
      var qtyInputs = Array.prototype.slice.call(pdp.querySelectorAll('[data-pdp-qty]'));
      var sticky = document.querySelector('[data-pdp-sticky-buy]');
      var stickyQty = sticky && sticky.querySelector('[data-pdp-sticky-qty]');
      var totals = Array.prototype.slice.call(pdp.querySelectorAll('[data-pdp-line-total]'))
        .concat(sticky ? Array.prototype.slice.call(sticky.querySelectorAll('[data-pdp-sticky-line-total]')) : []);
      var stickyQtyLabels = sticky ? Array.prototype.slice.call(sticky.querySelectorAll('[data-pdp-sticky-qty-label]')) : [];
      var primaryBuy = pdp.querySelector('[data-pdp-primary-buy]');
      var quickQtys = Array.prototype.slice.call(pdp.querySelectorAll('[data-pdp-qty-set]'));
      var quoteLinks = Array.prototype.slice.call(pdp.querySelectorAll('[data-pdp-quote-link]'));

      function cleanQty(value) {
        var qty = parseInt(value, 10);
        if (!Number.isFinite(qty) || qty < 1) qty = 1;
        return qty;
      }

      function currentQty() {
        var src = qtyInputs[0] || stickyQty;
        return cleanQty(src && src.value);
      }

      function syncQty(source) {
        var qty = cleanQty(source && source.value);
        qtyInputs.forEach(function (input) {
          if (input !== source) input.value = qty;
        });
        if (stickyQty && stickyQty !== source) stickyQty.value = qty;
        totals.forEach(function (el) {
          el.textContent = formatMoney(unitPrice * qty);
        });
        stickyQtyLabels.forEach(function (el) {
          el.textContent = String(qty);
        });
        quoteLinks.forEach(function (link) {
          try {
            var url = new URL(link.getAttribute('href'), window.location.origin);
            url.searchParams.set('qty', String(qty));
            url.searchParams.set('url', window.location.pathname);
            link.href = url.pathname + url.search;
          } catch (e) {}
        });
      }

      qtyInputs.forEach(function (input) {
        input.addEventListener('input', function () { syncQty(input); });
        input.addEventListener('change', function () { syncQty(input); });
      });
      if (stickyQty) {
        stickyQty.addEventListener('change', function () { syncQty(stickyQty); });
      }
      quickQtys.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var qty = cleanQty(btn.getAttribute('data-pdp-qty-set'));
          var input = qtyInputs[0] || stickyQty;
          if (!input) return;
          input.value = qty;
          input.dispatchEvent(new Event('change', { bubbles: true }));
          syncQty(input);
        });
      });
      syncQty(qtyInputs[0]);

      if (sticky && primaryBuy) {
        var ticking = false;
        function syncSticky() {
          ticking = false;
          var rect = primaryBuy.getBoundingClientRect();
          var visible = rect.bottom < 0 && currentQty() >= 1;
          sticky.classList.toggle('is-visible', visible);
          sticky.setAttribute('aria-hidden', visible ? 'false' : 'true');
        }
        function requestSyncSticky() {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(syncSticky);
        }
        window.addEventListener('scroll', requestSyncSticky, { passive: true });
        window.addEventListener('resize', requestSyncSticky);
        syncSticky();
      }
    });
  }

  function bindQuotePrefill(root) {
    (root || document).querySelectorAll('[data-quote-prefill]').forEach(function (card) {
      if (card.__quotePrefillBound) return;
      card.__quotePrefillBound = true;
      var params = new URLSearchParams(window.location.search || '');
      var product = params.get('product') || '';
      var sku = params.get('sku') || '';
      var qty = params.get('qty') || '';
      var productUrl = params.get('url') || '';
      var productInput = card.querySelector('[data-quote-product]');
      var skuInput = card.querySelector('[data-quote-sku]');
      var qtyInput = card.querySelector('[data-quote-qty]');
      var notesInput = card.querySelector('[data-quote-notes]');
      var status = card.querySelector('[data-quote-prefill-status]');
      var badge = card.querySelector('[data-quote-prefill-badge]');
      var productLink = card.querySelector('[data-quote-product-link]');
      var copyBtn = card.querySelector('[data-quote-copy]');

      if (productInput && product) productInput.value = product;
      if (skuInput && sku) skuInput.value = sku;
      if (qtyInput && qty) qtyInput.value = qty;
      if (notesInput && (product || sku || qty)) {
        notesInput.value = 'Please quote ' + (qty || 'bulk') + ' units' + (sku ? ' of SKU ' + sku : '') + (product ? ' (' + product + ')' : '') + '.';
      }
      if (product || sku || qty) {
        card.hidden = false;
        card.classList.add('is-prefilled');
        if (badge) badge.hidden = false;
        if (status) status.textContent = 'Review the product and quantity, then copy the request for your RMH3 rep.';
      }
      if (productLink && productUrl) {
        productLink.href = productUrl;
        productLink.hidden = false;
      }

      if (copyBtn) {
        copyBtn.addEventListener('click', function () {
          var text = [
            'Bulk quote request',
            productInput && productInput.value ? 'Product: ' + productInput.value : '',
            skuInput && skuInput.value ? 'SKU: ' + skuInput.value : '',
            qtyInput && qtyInput.value ? 'Quantity: ' + qtyInput.value : '',
            productLink && !productLink.hidden ? 'Product URL: ' + productLink.href : '',
            notesInput && notesInput.value ? 'Notes: ' + notesInput.value : ''
          ].filter(Boolean).join('\n');
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text).then(function () {
              copyBtn.innerHTML = icon('check', 14) + '<span>Copied</span>';
              window.setTimeout(function () {
                copyBtn.innerHTML = icon('box', 14) + '<span>Copy request</span>';
              }, 1400);
            }).catch(function () {
              window.prompt('Copy quote request', text);
            });
          } else {
            window.prompt('Copy quote request', text);
          }
        });
      }
    });
  }

  // Unit price where derivable — display only, no data writes. Conservative:
  // the title must contain exactly ONE "<count>/<pack word>" pattern (e.g.
  // "500/Box", "100/Pk", "25/pack"), count 2..10000, and a real price. Skips
  // anything ambiguous (multiple counts, count of 1, POR/gated prices).
  function packCount(text) {
    if (!text) return 0;
    var re = /(\d{2,5}|[2-9])\s*\/\s*(box|bx|pk|pack|pkg|case|cs|ct|bag|kit|carton|sleeve|roll|tub|jar|bottle|btl)\b/gi;
    var m;
    var counts = [];
    while ((m = re.exec(text))) counts.push(parseInt(m[1], 10));
    if (counts.length !== 1) return 0;
    return counts[0] >= 2 && counts[0] <= 10000 ? counts[0] : 0;
  }

  function parseDollarsToCents(text) {
    var m = /^\$([\d,]+\.\d{2})$/.exec(String(text || '').trim());
    return m ? Math.round(parseFloat(m[1].replace(/,/g, '')) * 100) : 0;
  }

  function unitNote(cents, n, compact) {
    var el = document.createElement(compact ? 'span' : 'div');
    el.className = 'unit-price-note' + (compact ? ' unit-price-note--card' : '');
    el.textContent = formatMoney(Math.max(1, Math.round(cents / n))) + ' / unit' + (compact ? '' : ' · ' + n + ' per pack');
    return el;
  }

  function bindUnitPrices(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-pdp-product]').forEach(function (pdp) {
      if (pdp.__unitPriceBound) return;
      pdp.__unitPriceBound = true;
      var unitEl = pdp.querySelector('[data-pdp-unit-price]');
      var titleEl = pdp.querySelector('[data-product-title]');
      var cents = parseInt(unitEl && unitEl.getAttribute('data-pdp-unit-price'), 10) || 0;
      var n = packCount(titleEl && titleEl.textContent);
      if (cents > 0 && n) unitEl.parentElement.insertAdjacentElement('afterend', unitNote(cents, n, false));
    });
    scope.querySelectorAll('.pcard').forEach(function (card) {
      if (card.__unitPriceBound) return;
      card.__unitPriceBound = true;
      var titleEl = card.querySelector('.ptitle');
      var priceEl = card.querySelector('.pprice .now');
      if (!titleEl || !priceEl) return;
      var cents = parseDollarsToCents(priceEl.textContent);
      var n = packCount(titleEl.textContent);
      if (cents > 0 && n) priceEl.insertAdjacentElement('afterend', unitNote(cents, n, true));
    });
    scope.querySelectorAll('.pcard-list').forEach(function (card) {
      if (card.__unitPriceBound) return;
      card.__unitPriceBound = true;
      var titleEl = card.querySelector('.plist-title');
      var priceEl = card.querySelector('.plist-price');
      if (!titleEl || !priceEl) return;
      var cents = parseDollarsToCents(priceEl.textContent);
      var n = packCount(titleEl.textContent);
      if (cents > 0 && n) priceEl.parentElement.insertAdjacentElement('afterend', unitNote(cents, n, true));
    });
  }

  // ---------------------------------------------------------------------------
  // PLP in-place AJAX filtering (progressive enhancement over the plain
  // <form method="get"> facet form + sort form + pagination + chips). Intercepts
  // facet toggles, price/sort/Apply submits, chip & "Clear all" removals and
  // pagination clicks; fetches the target URL, DOMParses it and swaps the CURRENT
  // .plp-grid innerHTML with the fetched one (rail active-states + counts + chips
  // + results + pager all refresh together). Then pushState, sync document.title,
  // re-bind via RMH3.init on the new nodes, preserve grid/list/table view and
  // smooth-scroll the results into view (never jump to page top). Handles
  // popstate the same way. ANY failure falls back to a normal navigation, and
  // the underlying <form method="get"> still works with JS off.
  // ---------------------------------------------------------------------------
  function bindPlpFilters() {
    if (document.__rmh3PlpFiltersBound) return;
    if (!document.querySelector('.plp-grid')) return; // not a faceted PLP
    document.__rmh3PlpFiltersBound = true;

    var GRID = '.plp-grid';
    var FORM = '#plp-filters';
    var reqToken = 0;
    var debounceTimer = null;

    function isMobile() {
      return !!(window.matchMedia && window.matchMedia('(max-width: 780px)').matches);
    }
    function grid() { return document.querySelector(GRID); }
    function facetForm() { return document.querySelector(FORM); }

    // Build a collection URL from a GET form's fields. Skips empty values so the
    // URL stays clean, and omits any page param so a filter/sort change resets to
    // page 1 — matching exactly what a native submit of the same form would send.
    function urlFromForm(form) {
      var params = new URLSearchParams();
      new FormData(form).forEach(function (v, k) {
        if (v === '' || v == null) return;
        params.append(k, v);
      });
      var qs = params.toString();
      var base = (form.getAttribute('action') || window.location.pathname).split('#')[0].split('?')[0];
      return qs ? base + '?' + qs : base;
    }

    // The sort <form> lives in .plp-head (OUTSIDE .plp-grid) so it is NOT
    // swapped — its hidden active-filter inputs go stale after an AJAX facet
    // change. So build the sort URL from the LIVE facet form's current filters
    // (which IS swapped, hence current) + the newly chosen sort_by + view. With
    // JS off, the form's own hidden inputs still drive a correct native submit.
    function urlForSort(sortForm) {
      var params = new URLSearchParams();
      var ff = facetForm();
      if (ff) {
        new FormData(ff).forEach(function (v, k) {
          if (v === '' || v == null || k === 'sort_by') return;
          params.append(k, v);
        });
      }
      var viewEl = sortForm.elements ? sortForm.elements.view : null;
      if (viewEl && viewEl.value) params.set('view', viewEl.value);
      var sb = sortForm.elements ? sortForm.elements.sort_by : null;
      if (sb && sb.value) params.set('sort_by', sb.value);
      var qs = params.toString();
      return qs ? window.location.pathname + '?' + qs : window.location.pathname;
    }

    function sameAsCurrent(url) {
      var a = document.createElement('a');
      a.href = url;
      return (a.pathname + a.search) === (window.location.pathname + window.location.search);
    }

    // Close the mobile filter sheet through the theme's own toggle machinery
    // (a [data-close="#plp-rail"] control invokes closePanel), so focus + the
    // backdrop are cleaned up exactly as a manual close would.
    function closeRail() {
      var rail = document.getElementById('plp-rail');
      if (!rail || !rail.classList.contains('open')) return;
      var closer = rail.querySelector('[data-close="#plp-rail"]') || document.querySelector('[data-close="#plp-rail"]');
      if (closer) closer.click();
    }

    // Re-apply the shopper's grid/list/table choice after a swap. The toggle
    // group lives in .plp-head (OUTSIDE .plp-grid) so its active state survives;
    // only the panels inside .plp-grid are new and need their display re-set.
    function reapplyView(scope) {
      var group = document.querySelector('[data-view-toggle]');
      var v = 'grid';
      if (group) {
        var active = group.querySelector('button[data-view].active') || group.querySelector('button[data-view]');
        if (active) v = active.getAttribute('data-view');
      }
      (scope || document).querySelectorAll('[data-view-panel]').forEach(function (p) {
        p.style.display = p.getAttribute('data-view-panel') === v ? '' : 'none';
      });
    }

    // Smooth-scroll the results head into view. force = true (pagination) always
    // scrolls; otherwise only when the head has scrolled above the sticky header,
    // so a facet tick never yanks the shopper away from the rail.
    function maybeScroll(force) {
      var anchor = document.getElementById('collection-products') || grid();
      if (!anchor) return;
      var headerH = 84;
      var top = anchor.getBoundingClientRect().top;
      if (force || top < headerH) {
        var y = window.pageYOffset + anchor.getBoundingClientRect().top - (headerH + 12);
        window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
      }
    }

    function plpNavigate(url, opts) {
      opts = opts || {};
      var current = grid();
      if (!current || !url) { window.location.assign(url || window.location.href); return; }
      var token = ++reqToken;
      var push = opts.push !== false && !sameAsCurrent(url);
      current.classList.add('is-loading');
      current.setAttribute('aria-busy', 'true');
      window.fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' }, credentials: 'same-origin' })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then(function (html) {
          if (token !== reqToken) return; // superseded by a newer request
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector(GRID);
          var live = grid();
          if (!fresh || !live) { window.location.assign(url); return; }
          live.innerHTML = fresh.innerHTML;
          if (doc.title) document.title = doc.title;
          if (push) { try { window.history.pushState({ rmh3plp: 1 }, '', url); } catch (e) {} }
          live.classList.remove('is-loading');
          live.removeAttribute('aria-busy');
          if (window.RMH3 && typeof RMH3.init === 'function') RMH3.init(live);
          reapplyView(live);
          maybeScroll(opts.scroll === true);
        })
        .catch(function () {
          if (token !== reqToken) return; // a newer request is already handling it
          window.location.assign(url); // graceful fallback to a full navigation
        });
    }

    // Desktop: each facet toggle applies immediately (debounced so ticking a few
    // boxes fires one request). Mobile batches via the sheet's Apply button.
    document.addEventListener('change', function (e) {
      var cb = e.target;
      if (!cb || cb.type !== 'checkbox' || !cb.closest || !cb.closest(FORM)) return;
      if (isMobile()) return;
      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(function () {
        var live = facetForm();
        if (live) plpNavigate(urlFromForm(live), { push: true, scroll: false });
      }, 250);
    });

    // Facet Apply / price Apply / mobile Apply (button form="plp-filters") and
    // the sort <select> (its inline onchange calls requestSubmit) all arrive here.
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.matches || !form.matches('form[data-plp-ajax]')) return;
      e.preventDefault();
      window.clearTimeout(debounceTimer); // an explicit apply supersedes a pending debounce
      var url;
      if (form.getAttribute('data-plp-ajax') === 'sort') {
        url = urlForSort(form);
      } else {
        if (form.id === 'plp-filters') closeRail();
        url = urlFromForm(form);
      }
      plpNavigate(url, { push: true, scroll: true });
    });

    // Chips, "Clear all", filtered-empty clear links, and pagination.
    document.addEventListener('click', function (e) {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var a = e.target.closest && e.target.closest('a[href]');
      if (!a || !a.closest(GRID)) return; // only within the PLP grid
      var isChip = a.classList.contains('plp-chip');
      var isClear = a.classList.contains('plp-facets__clear');
      var isLink = a.hasAttribute('data-plp-link');
      var isPager = !!a.closest('.plp-pagination');
      if (!isChip && !isClear && !isLink && !isPager) return;
      e.preventDefault();
      closeRail();
      var href = (a.getAttribute('href') || '').split('#')[0] || a.href;
      plpNavigate(href, { push: true, scroll: isPager });
    });

    // Back/forward across filter/sort/page states we created.
    window.addEventListener('popstate', function (e) {
      if (!e.state || !e.state.rmh3plp) return;
      if (!document.querySelector(GRID)) return;
      plpNavigate(window.location.href, { push: false, scroll: false });
    });

    // Mark the landing entry so popstate back to it is recognised as ours.
    try { window.history.replaceState({ rmh3plp: 1 }, '', window.location.href); } catch (e) {}
  }

  function init(root) {
    bindSteppers(root); bindTabs(root); bindSelectGroups(root);
    bindViewToggle(root); bindToggles(root); bindProductZoom(root); bindProductImages(root);
    bindMegaMenu(root); bindStickyHeader(); bindChildCategoriesPlacement(); bindSearchTypeahead(root); syncActiveNav();
    bindAjaxAdd(); bindCartMutations(); bindSkuAdd(); bindCartTools(); bindBestCarousel(root); bindPdpEnhancements(root); bindQuotePrefill(root);
    bindUnitPrices(root);
    bindPlpFilters();
  }

  document.addEventListener('DOMContentLoaded', function () {
    bootTokens(); init(document); bindPreviewSwitch();
    RMH3.cart.subscribe(function (cart) {
      updateHeaderCart(cart);
      renderDrawer(cart);
      renderCartPage(cart);
    });
    hydrateCart();
  });
  // Shopify theme editor: re-bind on section load
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });

  RMH3.applyAccent = applyAccent;
  RMH3.init = init;
  RMH3.cartRequest = cartRequest;
  RMH3.formatMoney = formatMoney;
  window.RMH3 = RMH3;
})();
